import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import {
  FscBasis,
  FscBracket,
  FscDefault,
  FscIndex,
  FscPayload,
  FscRate,
  FscSchedule,
  FscSide
} from './fsc.models';

type Resource = 'defaults' | 'bases' | 'index-values' | 'schedules' | 'brackets' | 'contact-rates' | 'side-rates';

interface Scope {
  side?: FscSide;
  contact_uuid?: string;
  schedule_uuid?: string;
  id?: string;
}

interface Envelope<T> {
  ok: boolean;
  data: T;
  message?: string;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class FscApiService {
  // Use the existing DAMS session -> per-user API-key bridge pattern.
  // API keys stay on the PHP server; AuthInterceptor supplies the DAMS JWT.
  private readonly url = '/api/rates/fsc_manager_v1.php';

  constructor(private readonly http: HttpClient) {}

  defaults(): Observable<FscDefault[]> {
    return this.request('GET', 'defaults');
  }

  bases(): Observable<FscBasis[]> {
    return this.request('GET', 'bases');
  }

  indexes(): Observable<FscIndex[]> {
    return this.request('GET', 'index-values');
  }

  schedules(): Observable<FscSchedule[]> {
    return this.request('GET', 'schedules');
  }

  rates(side: FscSide, contact_uuid: string): Observable<FscRate[]> {
    return this.request('GET', 'contact-rates', { side, contact_uuid });
  }

  schedule(id: string): Observable<FscSchedule> {
    return this.request('GET', 'schedules', { id });
  }

  brackets(schedule_uuid: string): Observable<FscBracket[]> {
    return this.request('GET', 'brackets', { schedule_uuid });
  }

  save<T>(resource: Resource, payload: FscPayload, scope: Scope = {}): Observable<T> {
    return this.request(scope.id ? 'PUT' : 'POST', resource, scope, payload);
  }

  deactivate<T>(resource: Resource, scope: Scope): Observable<T> {
    return this.request('DELETE', resource, scope);
  }

  side(side: FscSide): Observable<FscRate[]> {
    return this.request('GET', 'side-rates', { side });
  }

  private request<T>(
    method: string,
    resource: Resource,
    scope: Scope = {},
    body?: FscPayload
  ): Observable<T> {
    let params = new HttpParams().set('resource', resource);
    for (const [key, value] of Object.entries(scope)) if (value) params = params.set(key, value);
    return this.http.request<Envelope<T>>(method, this.url, { params, body }).pipe(
      map(response => {
        if (!response || response.ok !== true || response.data === undefined) {
          throw new Error(
            response?.message || response?.error || 'The FSC API returned an unexpected response.'
          );
        }
        return response.data;
      })
    );
  }
}
